import axios from 'axios';
import {isTokenValid} from '../../service/jwt/jwtUtil';
import {clearAllAuthData} from '../auth/authApiClient';
import {getAuthTokenKey, getBackendUrl, getRefreshTokenKey} from '../../utils/config';

// Use consistent token names
const refreshTokenName = getRefreshTokenKey();
const accessTokenName = getAuthTokenKey();

const authURLs = [
    '/api/auth/refresh-token',
    '/api/auth/login',
    '/api/auth/register',
    '/api/auth/reset-password',
    '/api/auth/reset-otp',
    '/api/auth/verify-otp',
];

const axiosApiClient = axios.create({
    baseURL: getBackendUrl(),
    timeout: 30000, // 30 second timeout
    headers: {
        'Content-Type': 'application/json',
    },
});

// Request interceptor
axiosApiClient.interceptors.request.use(config => {
    const token = localStorage.getItem(accessTokenName);

    // Only add token if it's valid and not an auth URL
    if (token && isTokenValid(token) && !authURLs.includes(config.url)) {
        config.headers['Authorization'] = `Bearer ${token}`;
        config.headers['Referrer-Policy'] = 'strict-origin-when-cross-origin';
    }
    return config;
});

// Response interceptor for automatic token refresh
axiosApiClient.interceptors.response.use(
    (response) => response,
    async (error) => {
        const originalRequest = error.config;

        // Handle 401 and 403 errors and avoid infinite loops
        if ((error.response?.status === 401 || error.response?.status === 403) && !originalRequest._retry && !authURLs.includes(originalRequest.url)) {
            originalRequest._retry = true;

            try {
                const refreshToken = localStorage.getItem(refreshTokenName);
                if (refreshToken) {
                    console.log('Attempting to refresh token via axios interceptor');
                    const response = await axios.post(
                        `${getBackendUrl()}/api/auth/refresh-token`,
                        {refreshToken}
                    );

                    const {token, refreshToken: newRefreshToken} = response.data;
                    localStorage.setItem(accessTokenName, token);
                    localStorage.setItem(refreshTokenName, newRefreshToken);

                    // Retry the original request with new token
                    originalRequest.headers['Authorization'] = `Bearer ${token}`;
                    return axiosApiClient(originalRequest);
                } else {
                    // No refresh token available, clear everything and redirect
                    console.log('No refresh token available, clearing auth data and redirecting');
                    clearAllAuthData();
                    // Use window.location.href for immediate redirect without React Router
                    window.location.href = '/authentication/sign-in';
                }
            } catch (refreshError) {
                console.error('Token refresh failed in axios interceptor:', refreshError);
                // Clear tokens and redirect to login
                console.log('Token refresh failed, clearing auth data and redirecting');
                clearAllAuthData();
                // Use window.location.href for immediate redirect without React Router
                window.location.href = '/authentication/sign-in';
                return Promise.reject(refreshError);
            }
        }

        return Promise.reject(error);
    }
);

export default axiosApiClient;
